import type { VideoJsPlayer } from 'video.js';

import type { BrowserStats } from './use-browser-stats';
import type { PlayerStats } from './use-player-stats';
import type { PlayerOptions } from '../player/player.interface';

import { useEffect, useState } from 'react';

import { useBrowserStats } from './use-browser-stats';
import { useDownloadJSON } from './use-download';
import { usePlayerStats } from './use-player-stats';
import { memoryLogs } from '../../utils/logger';

import styles from './player-stats.module.css';

export function PlayerStats({ player, playerOptions }: { player: VideoJsPlayer; playerOptions: PlayerOptions }) {
  const { downloadJSON, downloadJSONInNewTab } = useDownloadJSON();
  const [statsLogs, setStatsLogs] = useState<{ time: string; vhsStats: PlayerStats; browserStats: BrowserStats }[]>([]);
  const vhsStats = usePlayerStats(player);
  const browserStats = useBrowserStats();

  useEffect(() => {
    if (!browserStats || !vhsStats) {
      return;
    }

    setStatsLogs((prevLogs) => {
      // Avoid duplicate logs
      if (
        prevLogs.length > 0 &&
        JSON.stringify(vhsStats) === JSON.stringify(prevLogs[prevLogs.length - 1].vhsStats) &&
        JSON.stringify(browserStats) === JSON.stringify(prevLogs[prevLogs.length - 1].browserStats)
      ) {
        return prevLogs;
      }

      return [
        ...prevLogs,
        {
          time: new Date().toISOString(),
          vhsStats,
          browserStats,
        },
      ];
    });
  }, [vhsStats, browserStats]);

  return (
    vhsStats && (
      <div className={styles.wrapper}>
        <div>
          <div>Video Id:</div>
          <span>{playerOptions.metadata?.videoId}</span>
        </div>

        <div>
          <div>Browser name:</div>
          <span>{browserStats?.browserInfo?.browserName}</span>
        </div>

        <div>
          <div>User Agent:</div>
          <span>{browserStats?.userAgent}</span>
        </div>

        <div>
          <div>Platform:</div>
          <span>{browserStats?.navigatorMetrics?.platform}</span>
        </div>

        <div>
          <div>Brands:</div>
          <span>
            {Array.isArray(browserStats?.navigatorMetrics?.brands)
              ? browserStats.navigatorMetrics.brands.map((brandInfo, index) => (
                  <span key={brandInfo.brand ?? index}>
                    {brandInfo.brand ?? ''}
                    {brandInfo.version ? ` (${brandInfo.version})` : ''}
                    {index < (browserStats.navigatorMetrics?.brands.length ?? 0) - 1 ? ', ' : ''}
                  </span>
                ))
              : ''}
          </span>
        </div>

        <div>
          <div>Viewport:</div>
          <span>{`${vhsStats.viewport.width}x${vhsStats.viewport.height}${vhsStats.viewport.density ? '*' + vhsStats.viewport.density : ''}`}</span>
        </div>

        {vhsStats.representations.length > 0 && (
          <>
            <div>
              <div>Video Bitrate:</div>
              <span>
                {`${vhsStats.videoBitrate.toLocaleString(undefined, {
                  maximumFractionDigits: 1,
                })} kbps`}
              </span>
            </div>

            <div>
              <div>Measured Bitrate:</div>
              <span>
                {`${vhsStats.measuredBitrate.toLocaleString(undefined, {
                  maximumFractionDigits: 1,
                })} kbps`}
              </span>
            </div>

            <div>
              <div>Representations:</div>
              <span>
                {vhsStats.representations.map((rep, index) => (
                  <div
                    key={index}
                    style={vhsStats.videoBitrate === rep.bandwidth ? { color: 'lightgreen' } : undefined}
                  >
                    <strong>
                      {rep.width}x{rep.height}
                    </strong>
                    <br />
                    {`${rep.bandwidth.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })} kbps, Video Codec: ${rep.videoCodec || 'N/A'}, Audio Codec: ${rep.audioCodec || 'N/A'}`}
                  </div>
                ))}
              </span>
            </div>
          </>
        )}

        <div style={{ flex: 1, alignItems: 'end', justifyContent: 'end' }}>
          <button onClick={() => downloadJSON(statsLogs, 'stats-logs')}>dl stats</button>
          <button onClick={() => downloadJSONInNewTab(statsLogs, 'stats-logs')}>dl stats (new tab)</button>
          {memoryLogs.length > 0 && (
            <>
              <button onClick={() => downloadJSON(memoryLogs, 'console-logs')}>dl logs</button>
              <button onClick={() => downloadJSONInNewTab(memoryLogs, 'console-logs')}>dl logs (new tab)</button>
            </>
          )}
        </div>
      </div>
    )
  );
}
